import React, { createContext, useContext, useState, ReactNode } from 'react';

interface CartItem {
  id: string;
  name: string;
  price: number;
  currency: string;
  type: string;
  quantity: number;
  [key: string]: any;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity'>) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  totalItems: number;
  openPurchaseModal: () => void;
  closePurchaseModal: () => void;
  isPurchaseModalOpen: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);

  const addToCart = (item: Omit<CartItem, 'quantity'>) => {
    setItems((prevItems) => {
      // For multi-reset and extension items, always add as separate entries
      if ((item as any).isMultiReset || (item as any).isMultiExtension) {
        return [...prevItems, { ...item, quantity: 1 }];
      }

      const existingItem = prevItems.find((i) => i.id === item.id);
      if (existingItem) {
        if (existingItem.quantity >= 10) return prevItems;
        return prevItems.map((i) =>
          i.id === item.id ? { ...i, quantity: Math.min(i.quantity + 1, 10) } : i
        );
      }
      return [...prevItems, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (itemId: string) => {
    setItems((prevItems) => {
      const exactMatchIndex = prevItems.findIndex((item) =>
        item.id === itemId || String(item.id) === String(itemId)
      );
      if (exactMatchIndex !== -1) {
        return prevItems.filter((_, index) => index !== exactMatchIndex);
      }

      if (itemId.includes('-')) {
        const lastDashIndex = itemId.lastIndexOf('-');
        const productId = itemId.substring(0, lastDashIndex);
        const accountNumber = itemId.substring(lastDashIndex + 1);

        const specialItemIndex = prevItems.findIndex(
          (item) =>
            (String(item.id) === String(productId) || item.id === productId) &&
            (item as any).accountNumber === accountNumber &&
            ((item as any).isMultiReset === true || (item as any).isMultiExtension === true)
        );

        if (specialItemIndex !== -1) {
          return prevItems.filter((_, index) => index !== specialItemIndex);
        }
      }

      return prevItems;
    });
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    const clamped = Math.min(quantity, 10);
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, quantity: clamped } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const openPurchaseModal = () => {
    setIsPurchaseModalOpen(true);
  };

  const closePurchaseModal = () => {
    setIsPurchaseModalOpen(false);
  };

  const itemCount = items.length;
  const totalItems = items.reduce((total, item) => {
    const quantity = ((item as any).isMultiReset || (item as any).isMultiExtension) ? 1 : item.quantity;
    return total + quantity;
  }, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        itemCount,
        totalItems,
        openPurchaseModal,
        closePurchaseModal,
        isPurchaseModalOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
